import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { Download, Printer, ArrowLeft } from 'lucide-react';
import Button from '../../components/Button';
import SimpleReportDocument from '../../components/SimpleReportDocument';
import {
  downloadSimpleReportPDF,
  printSimpleReportPDF,
} from '../../utils/pdfGeneratorSimple';

const ReportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (location.state) {
      setReportData(location.state);
      setLoading(false);
    } else {
      navigate('/reports');
    }
  }, [location, navigate]);

  const clientInfo = reportData
    ? { name: reportData.clientName, company: reportData.clientCompany }
    : null;

  const handlePrint = () => {
    if (!reportData || !clientInfo) return;
    try {
      setPrinting(true);
      printSimpleReportPDF(
        reportData.data,
        reportData.reportType,
        clientInfo,
        reportData.generatedAt
      );
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast.error(error.message || 'Erreur lors de l’impression');
    } finally {
      setPrinting(false);
    }
  };

  const handleDownload = () => {
    if (!reportData || !clientInfo) return;
    try {
      downloadSimpleReportPDF(
        reportData.data,
        reportData.reportType,
        clientInfo,
        reportData.generatedAt
      );
      toast.success('Téléchargement du rapport…');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.message || 'Erreur lors de la génération du PDF');
    }
  };

  if (loading || !reportData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du rapport...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white print:min-h-0">
      <div className="print:hidden bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/reports')}>
              Retour aux rapports
            </Button>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                icon={Printer}
                onClick={handlePrint}
                loading={printing}
                disabled={printing}
              >
                Imprimer
              </Button>
              <Button variant="primary" icon={Download} onClick={handleDownload}>
                Télécharger PDF
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500 max-w-2xl">
            L’aperçu correspond au PDF. Le bouton Imprimer utilise le même document que le
            téléchargement PDF.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:max-w-none print:px-0 print:py-0">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none print:rounded-none">
          <SimpleReportDocument
            reportData={reportData.data}
            reportType={reportData.reportType}
            clientName={reportData.clientName}
            clientCompany={reportData.clientCompany}
            generatedAt={reportData.generatedAt}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;
